// src/pages/portal/PortalRecentTransactionsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout, { NAV_FEATURE_MAP, NAV_PERMISSION_MAP } from './PortalLayout';
import DeletionConfirmModal from '../../components/common/DeletionConfirmModal';
import useDeletionConfirm from '../../hooks/useDeletionConfirm';
import { transactionService } from '../../services/api';
import tenantStorage from '../../services/tenantStorage';
import { useLanguage, translateDynamic } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import {
    generateReceiptHTML,
    printWithIframe,
} from '../../utils/PrintingUtils';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import MobileHero from '../../components/common/MobileHero';
import PortalLinkTabs, { buildTransactionsVoidsTabs } from '../../components/common/PortalLinkTabs';
import { C } from '../../styles/theme';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

const PAGE_SIZE = 25;

// ─── Date helpers (local time only — never toISOString/UTC) ───────────────────
const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const getToday = () => fmtDate(new Date());
const addDays = (iso, delta) => {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + delta);
    return fmtDate(dt);
};
const startOfWeek = () => {
    const dt = new Date();
    const day = (dt.getDay() + 6) % 7; // Monday = 0
    dt.setDate(dt.getDate() - day);
    return fmtDate(dt);
};
const startOfMonth = () => {
    const dt = new Date();
    return fmtDate(new Date(dt.getFullYear(), dt.getMonth(), 1));
};

// ─── Shared inline components ─────────────────────────────────────────────────

const StatCard = ({ icon, label, value, sub, accent = C.green, accentBg = C.greenLight }) => (
    <div style={{
        background: C.white, borderRadius: 12, padding: '20px 24px',
        border: `1px solid ${C.border}`, flex: 1, minWidth: 180,
        fontFamily: FONT,
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: accent, flexShrink: 0,
            }}>
                {icon}
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 2 }}>
                    {label}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1.2 }}>
                    {value}
                </div>
                {sub && <div style={{ fontSize: 12, color: C.mutedLight, marginTop: 2 }}>{sub}</div>}
            </div>
        </div>
    </div>
);

const EmptyState = ({ icon, title, subtitle }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '80px 24px', textAlign: 'center',
        fontFamily: FONT,
    }}>
        <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: C.greenLight, display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 20, color: C.green,
        }}>
            {icon}
        </div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>{title}</h3>
        <p style={{ fontSize: 14, color: C.muted, margin: 0, maxWidth: 360, lineHeight: 1.6 }}>{subtitle}</p>
    </div>
);

const SelectFilter = ({ value, onChange, options, placeholder, style: extraStyle }) => (
    <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
            padding: '7px 12px', borderRadius: 8, border: `1px solid ${C.border}`,
            fontSize: 13, color: value ? C.text : C.mutedLight, background: C.white,
            fontFamily: FONT, outline: 'none', cursor: 'pointer', minWidth: 120,
            ...extraStyle,
        }}
    >
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
);

// ─── Icons (SVG, no emoji) ────────────────────────────────────────────────────
const IconReceipt = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 2v20l4-2 4 2 4-2 4 2V2l-4 2-4-2-4 2z"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/>
    </svg>
);
const IconCount = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
    </svg>
);
const IconCard = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
);
const IconNoData = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 2v20l4-2 4 2 4-2 4 2V2l-4 2-4-2-4 2z"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/>
    </svg>
);
const IconSearch = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);
const IconRefresh = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
);

// ─── Main component ───────────────────────────────────────────────────────────
const PortalRecentTransactionsPage = () => {
    const { tenantConfig, getCurrency, hasPermission, hasFeature } = useAuth();
    const { t } = useLanguage();
    const linkTabs = buildTransactionsVoidsTabs({ t, hasPermission, hasFeature, navFeatureMap: NAV_FEATURE_MAP, navPermissionMap: NAV_PERMISSION_MAP });
    const currency = getCurrency();
    const isMobile = useIsMobile();

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState({ revenue: 0, count: 0, cash: 0, card: 0 });
    const [filterOptions, setFilterOptions] = useState({ staff: [], payment_methods: [], dining_options: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Current business day (drives default range + which rows are editable)
    const [currentSalesDate, setCurrentSalesDate] = useState('');

    // Date range
    const [preset, setPreset] = useState('today');
    const [startDate, setStartDate] = useState(getToday);
    const [endDate, setEndDate] = useState(getToday);

    // Filters / sort / paging
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCashier, setFilterCashier] = useState('');
    const [filterPayment, setFilterPayment] = useState('');
    const [filterDining, setFilterDining] = useState('');
    const [sortField, setSortField] = useState('time');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);

    // Reprint modal state
    const [reprintModalOpen, setReprintModalOpen] = useState(false);
    const [transactionToReprint, setTransactionToReprint] = useState(null);
    const [selectedReceiptTypes, setSelectedReceiptTypes] = useState({ customer: true, cashier: true });

    const deletionConfirm = useDeletionConfirm();

    // Escape key to close reprint modal
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape' && reprintModalOpen) setReprintModalOpen(false); };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [reprintModalOpen]);

    // ─── Data loading ─────────────────────────────────────────────────────────

    // Resolve the current business day once, to default the range and to
    // decide which rows allow reprint/void. Falls back to today's date.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const bd = await tenantStorage.getBusinessDayState();
                if (cancelled) return;
                if (bd?.isBusinessDay && bd?.salesDate) {
                    setCurrentSalesDate(bd.salesDate);
                    setStartDate(bd.salesDate);
                    setEndDate(bd.salesDate);
                }
            } catch (err) {
                console.error('Error resolving business day:', err);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await transactionService.getTransactionsRange({
                startDate, endDate,
                limit: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
                staff: filterCashier,
                paymentMethod: filterPayment,
                diningOption: filterDining,
                search: searchQuery,
                sortField, sortDir,
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setSummary(data.summary || { revenue: 0, count: 0, cash: 0, card: 0 });
            setFilterOptions(data.filters || { staff: [], payment_methods: [], dining_options: [] });
        } catch (err) {
            console.error('Error loading transactions:', err);
            setError(t('failed_to_load_daily_data'));
            setItems([]); setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, page, filterCashier, filterPayment, filterDining, searchQuery, sortField, sortDir, t]);

    useEffect(() => {
        const id = setTimeout(fetchData, searchQuery ? 300 : 0);
        return () => clearTimeout(id);
    }, [fetchData, searchQuery]);

    // Reset to page 1 whenever the range, filters, or sort change.
    useEffect(() => { setPage(1); }, [startDate, endDate, filterCashier, filterPayment, filterDining, searchQuery, sortField, sortDir]);

    const applyPreset = useCallback((key) => {
        setPreset(key);
        const today = getToday();
        if (key === 'today') { setStartDate(today); setEndDate(today); }
        else if (key === 'yesterday') { const y = addDays(today, -1); setStartDate(y); setEndDate(y); }
        else if (key === 'week') { setStartDate(startOfWeek()); setEndDate(today); }
        else if (key === 'month') { setStartDate(startOfMonth()); setEndDate(today); }
        // 'custom' leaves the date inputs as-is
    }, []);

    const shiftDay = useCallback((delta) => {
        setPreset('custom');
        setStartDate((s) => {
            const next = addDays(s, delta);
            setEndDate(next);
            return next;
        });
    }, []);

    const handleExportCSV = useCallback(async () => {
        try {
            const data = await transactionService.getTransactionsRange({
                startDate, endDate, limit: 200, offset: 0,
                staff: filterCashier, paymentMethod: filterPayment,
                diningOption: filterDining, search: searchQuery,
                sortField, sortDir,
            });
            const rows = data.items || [];
            let csv = 'ID,Staff,Payment,Date,Time,Items,Amount,Discount\n';
            rows.forEach((r) => {
                csv += `${r.transaction_id},"${r.staff_name || ''}",${r.payment_method},${r.date},${r.time},${r.items_count},${r.final_amount},${r.discount_amount}\n`;
            });
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `transactions-${startDate}-to-${endDate}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('CSV export failed:', err);
            setError(t('failed_to_load_daily_data'));
        }
    }, [startDate, endDate, filterCashier, filterPayment, filterDining, searchQuery, sortField, sortDir, t]);

    // ─── Delete / Void handler ────────────────────────────────────────────────
    const handleDeleteTransaction = async (transactionId, reason) => {
        try {
            setLoading(true);
            await transactionService.deleteTransaction(transactionId, reason);
            await fetchData();
            setSuccess(t('transaction_deleted_successfully'));
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setLoading(false);
            if (err.response?.status === 403) {
                setError(t('insufficient_permissions_delete_transaction'));
            } else {
                setError(t('failed_to_delete_transaction'));
            }
            throw err;
        }
    };

    // ─── Reprint handler ──────────────────────────────────────────────────────
    const handleReprintReceipt = async (transaction) => {
        let txCopy = { ...transaction };
        // Range rows are lightweight (no nested items); hydrate from the current
        // day's full transactions so the reprinted receipt has line items.
        if (!Array.isArray(txCopy.items) || txCopy.items.length === 0) {
            try {
                const dayTx = await transactionService.getTransactionsAfterStartOfDay(currentSalesDate);
                const full = (dayTx || []).find((d) => d.transaction_id === transaction.transaction_id);
                if (full) txCopy = { ...full };
            } catch (err) {
                console.error('Failed to load receipt items:', err);
            }
        }
        if (!txCopy.products && Array.isArray(txCopy.items) && txCopy.items.length > 0) {
            txCopy.products = txCopy.items.map(item => ({
                name: item.product_name,
                quantity: parseInt(item.quantity) || 1,
                selling_price: parseFloat(item.unit_price) || 0,
                category: item.category || t('unknown'),
            }));
        }

        const printableCategories = tenantConfig?.printable_categories || [];
        const availableCategories = [...new Set(txCopy.products?.map(p => p.category) || [])];
        const initial = { customer: true, cashier: true };
        printableCategories.forEach(cat => {
            if (availableCategories.includes(cat)) initial[cat.toLowerCase().replace(/\s+/g, '_')] = true;
        });
        if (availableCategories.some(c => !printableCategories.includes(c))) initial.others = true;

        setSelectedReceiptTypes(initial);
        setTransactionToReprint(txCopy);
        setReprintModalOpen(true);
    };

    const handlePrintSelected = () => {
        if (!transactionToReprint) return;
        const brandName = tenantConfig?.brand_name || 'Aqana';
        const receiptData = {
            date: transactionToReprint.date,
            time: transactionToReprint.time,
            cashier: transactionToReprint.staff_name,
            paymentMethod: transactionToReprint.payment_method,
            diningOption: transactionToReprint.dining_option,
            tableNumber: transactionToReprint.table_number,
            products: transactionToReprint.products || [],
            subtotal: parseFloat(transactionToReprint.total_amount) || 0,
            discount: parseFloat(transactionToReprint.discount_amount) || 0,
            serviceCharge: transactionToReprint.service_charge
                ? (parseFloat(transactionToReprint.total_amount) * (tenantConfig?.service_charge_rate || 0.12)) : 0,
            total: parseFloat(transactionToReprint.final_amount) || 0,
            notes: transactionToReprint.notes || '',
        };
        const tenantInfo = {
            brand_name: brandName, currency,
            printable_categories: tenantConfig?.printable_categories || [],
            receipt_header: tenantConfig?.receipt_header,
            receipt_footer: tenantConfig?.receipt_footer,
            receipt_logo: tenantConfig?.receipt_logo,
        };

        const receipts = [];
        if (selectedReceiptTypes.customer) receipts.push(generateReceiptHTML(receiptData, 'CUSTOMER RECEIPT', false, tenantInfo));
        if (selectedReceiptTypes.cashier) receipts.push(generateReceiptHTML(receiptData, 'CASHIER COPY', false, tenantInfo));

        const printableCategories = tenantConfig?.printable_categories || [];
        printableCategories.forEach(cat => {
            const key = cat.toLowerCase().replace(/\s+/g, '_');
            if (selectedReceiptTypes[key]) {
                const items = receiptData.products.filter(p => p.category === cat);
                if (items.length > 0) receipts.push(generateReceiptHTML({ ...receiptData, products: items }, `${cat.toUpperCase()} ORDER`, true, tenantInfo));
            }
        });
        if (selectedReceiptTypes.others) {
            const other = receiptData.products.filter(p => !printableCategories.includes(p.category));
            if (other.length > 0) receipts.push(generateReceiptHTML({ ...receiptData, products: other }, 'OTHER ITEMS', true, tenantInfo));
        }

        if (receipts.length === 0) { setError(t('select_at_least_one_receipt')); return; }
        printWithIframe(receipts.map(r => `<div class="print-page">${r}</div>`).join(''), `${brandName.toUpperCase()} Receipts`);
        setReprintModalOpen(false);
    };

    // ─── Server-derived summary, options, paging ──────────────────────────────
    const stats = { total: summary.revenue, count: summary.count, cash: summary.cash, card: summary.card };
    const cashiers = filterOptions.staff;
    const paymentMethods = filterOptions.payment_methods;
    const diningOptions = filterOptions.dining_options;
    const hasActiveFilters = searchQuery || filterCashier || filterPayment || filterDining;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // Rows are editable (reprint/void) only on the current business day.
    const isCurrentDay = startDate === currentSalesDate && endDate === currentSalesDate && !!currentSalesDate;

    const toggleSort = (field) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('desc'); }
    };

    const SortArrow = ({ field }) => {
        if (sortField !== field) return <span style={{ color: C.mutedLight, marginLeft: 4 }}>{'\u2195'}</span>;
        return <span style={{ color: C.green, marginLeft: 4 }}>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>;
    };

    const getProductDisplay = (row) => {
        const items = row.items && row.items.length ? row.items : [];
        if (items.length === 0) {
            if (row.products_summary) {
                const names = row.products_summary.split(', ').map(i => i.replace(/^\d+x\s/, '').trim()).filter(Boolean);
                return names.length > 1
                    ? <>{translateDynamic(names[0], t)} <span style={{ color: C.blue, fontSize: 12 }}>+{names.length - 1}</span></>
                    : translateDynamic(names[0], t);
            }
            return <span style={{ color: C.mutedLight }}>—</span>;
        }
        if (items.length === 1) return translateDynamic(items[0].product_name, t);
        return <>{translateDynamic(items[0].product_name, t)} <span style={{ color: C.blue, fontSize: 12 }}>+{items.length - 1}</span></>;
    };

    const paymentBadge = (pm) => {
        const map = {
            Cash: { bg: C.greenLight, color: C.green },
            Card: { bg: C.blueLight, color: C.blue },
            'Split Payment': { bg: C.purpleLight, color: C.purple },
        };
        const col = map[pm] || { bg: C.amberLight, color: C.amber };
        return (
            <span style={{
                fontSize: 11, fontWeight: 600, letterSpacing: 0.3, borderRadius: 5, padding: '2px 8px',
                background: col.bg, color: col.color, fontFamily: FONT, whiteSpace: 'nowrap',
            }}>
                {translateDynamic(pm, t)}
            </span>
        );
    };

    // ─── Table header cell style ──────────────────────────────────────────────
    const TH = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, padding: '12px 16px', textAlign: 'left', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap', fontFamily: FONT };
    const TD = { fontSize: 13, color: C.text, padding: '14px 16px', borderBottom: `1px solid ${C.border}`, fontFamily: FONT, verticalAlign: 'middle' };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <PortalLayout activeId="transactions">
            <div style={{ fontFamily: FONT }}>
                <PortalLinkTabs tabs={linkTabs} active="transactions" />

                {/* Header (mobile) */}
                {isMobile ? (
                    <div style={{ marginBottom: 18 }}>
                        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>{t('recent_transactions')}</h1>
                        <p style={{ fontSize: 13, color: C.muted, margin: '3px 0 14px' }}>{t('browse_all_transactions')}</p>
                        {/* Period row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <button onClick={() => shiftDay(-1)} title={t('previous_day')}
                                style={{ width: 36, height: 36, borderRadius: 9, border: `1.5px solid ${C.border}`, background: C.white, color: C.text, cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>{'‹'}</button>
                            <div className="portal-hscroll" style={{ display: 'flex', gap: 6, flex: 1, paddingBottom: 2 }}>
                                {[
                                    { key: 'today', label: t('today') },
                                    { key: 'yesterday', label: t('yesterday') },
                                    { key: 'week', label: t('this_week') },
                                    { key: 'month', label: t('this_month') },
                                    { key: 'custom', label: t('custom') },
                                ].map((p) => (
                                    <button key={p.key} onClick={() => applyPreset(p.key)}
                                        style={{ flexShrink: 0, padding: '8px 14px', borderRadius: 20, border: `1.5px solid ${preset === p.key ? C.green : C.border}`, background: preset === p.key ? C.green : C.white, color: preset === p.key ? '#fff' : C.muted, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: FONT }}>
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <button onClick={() => shiftDay(1)} title={t('next_day')}
                                style={{ width: 36, height: 36, borderRadius: 9, border: `1.5px solid ${C.border}`, background: C.white, color: C.text, cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>{'›'}</button>
                        </div>
                        {preset === 'custom' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                                    style={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: 13, padding: '9px 10px', border: `1.5px solid ${C.border}`, borderRadius: 8, outline: 'none' }} />
                                <span style={{ fontSize: 12, color: C.muted }}>{t('to')}</span>
                                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                                    style={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: 13, padding: '9px 10px', border: `1.5px solid ${C.border}`, borderRadius: 8, outline: 'none' }} />
                            </div>
                        )}
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={fetchData} disabled={loading}
                                style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px', borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.white, color: C.text, fontSize: 13, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1, fontFamily: FONT }}>
                                <IconRefresh /> {loading ? t('refreshing') : t('refresh')}
                            </button>
                            <button onClick={handleExportCSV} disabled={loading || items.length === 0}
                                style={{ flex: 1, padding: '11px', borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.white, color: C.text, fontSize: 13, fontWeight: 700, cursor: (loading || items.length === 0) ? 'default' : 'pointer', opacity: (loading || items.length === 0) ? 0.5 : 1, fontFamily: FONT }}>
                                {t('export_csv')}
                            </button>
                        </div>
                    </div>
                ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                    <div>
                        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>
                            {t('recent_transactions')}
                        </h1>
                        <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>
                            {t('browse_all_transactions')}
                        </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button onClick={() => shiftDay(-1)} title={t('previous_day')}
                            style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.text, cursor: 'pointer', fontSize: 16 }}>
                            {'‹'}
                        </button>
                        <div style={{ display: 'inline-flex', background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                            {[
                                { key: 'today', label: t('today') },
                                { key: 'yesterday', label: t('yesterday') },
                                { key: 'week', label: t('this_week') },
                                { key: 'month', label: t('this_month') },
                                { key: 'custom', label: t('custom') },
                            ].map((p) => (
                                <button key={p.key} onClick={() => applyPreset(p.key)}
                                    style={{
                                        fontFamily: FONT, fontSize: 12, fontWeight: 600, padding: '7px 12px', border: 'none', cursor: 'pointer',
                                        background: preset === p.key ? C.green : 'transparent',
                                        color: preset === p.key ? C.white : C.muted,
                                    }}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => shiftDay(1)} title={t('next_day')}
                            style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.text, cursor: 'pointer', fontSize: 16 }}>
                            {'›'}
                        </button>
                        {preset === 'custom' && (
                            <>
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                                    style={{ fontFamily: FONT, fontSize: 12, padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 8, outline: 'none' }} />
                                <span style={{ fontSize: 12, color: C.muted }}>{t('to')}</span>
                                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                                    style={{ fontFamily: FONT, fontSize: 12, padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 8, outline: 'none' }} />
                            </>
                        )}
                        <button onClick={fetchData} disabled={loading}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
                                border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: 13, fontWeight: 600,
                                cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1, fontFamily: FONT,
                            }}>
                            <IconRefresh /> {loading ? t('refreshing') : t('refresh')}
                        </button>
                        <button onClick={handleExportCSV} disabled={loading || items.length === 0}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
                                border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: 13, fontWeight: 700,
                                cursor: (loading || items.length === 0) ? 'default' : 'pointer', opacity: (loading || items.length === 0) ? 0.5 : 1, fontFamily: FONT,
                            }}>
                            {t('export_csv')}
                        </button>
                    </div>
                </div>
                )}

                {/* Alerts */}
                {error && (
                    <div style={{
                        padding: '12px 16px', borderRadius: 8, background: C.redLight,
                        color: C.red, fontSize: 13, fontWeight: 600, marginBottom: 16,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: FONT,
                    }}>
                        {error}
                        <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>&times;</button>
                    </div>
                )}
                {success && (
                    <div style={{
                        padding: '12px 16px', borderRadius: 8, background: C.greenLight,
                        color: C.greenDark, fontSize: 13, fontWeight: 600, marginBottom: 16, fontFamily: FONT,
                    }}>
                        {success}
                    </div>
                )}

                {(
                    <>
                        {/* Summary cards */}
                        {isMobile ? (
                            <MobileHero
                                label={t('total_revenue')}
                                value={`${currency}${stats.total.toFixed(2)}`}
                                meta={`${stats.count} ${stats.count === 1 ? t('transaction') : t('transactions')}${stats.count > 0 ? ` · ${t('avg')} ${currency}${(stats.total / stats.count).toFixed(2)}` : ''}`}
                                stats={[
                                    { label: t('cash_sales'), value: `${currency}${stats.cash.toFixed(2)}` },
                                    { label: t('card_sales'), value: `${currency}${stats.card.toFixed(2)}` },
                                ]}
                            />
                        ) : (
                            <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                                <StatCard icon={<span style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{currency}</span>} label={t('total_revenue')} value={`${currency}${stats.total.toFixed(2)}`} accent={C.green} accentBg={C.greenLight} />
                                <StatCard icon={<IconCount />} label={t('transactions')} value={stats.count} sub={stats.count > 0 ? `${t('avg')}: ${currency}${(stats.total / stats.count).toFixed(2)}` : null} accent={C.blue} accentBg={C.blueLight} />
                                <StatCard icon={<IconReceipt />} label={t('cash_sales')} value={`${currency}${stats.cash.toFixed(2)}`} accent={C.green} accentBg={C.greenLight} />
                                <StatCard icon={<IconCard />} label={t('card_sales')} value={`${currency}${stats.card.toFixed(2)}`} accent={C.blue} accentBg={C.blueLight} />
                            </div>
                        )}

                        {/* Transactions table card */}
                        <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>

                            {/* ── Toolbar: search + filters ── */}
                            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                                {/* Search */}
                                <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
                                    <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.mutedLight }}>
                                        <IconSearch />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder={t('search_transactions')}
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        style={{
                                            width: '100%', padding: '8px 12px 8px 34px', borderRadius: 8,
                                            border: `1px solid ${C.border}`, fontSize: 13, fontFamily: FONT,
                                            outline: 'none', color: C.text, background: C.bg,
                                        }}
                                    />
                                </div>

                                {/* Filter dropdowns */}
                                <SelectFilter value={filterCashier} onChange={setFilterCashier} options={cashiers} placeholder={t('all_cashiers')} />
                                <SelectFilter value={filterPayment} onChange={setFilterPayment} options={paymentMethods} placeholder={t('all_payments')} />
                                <SelectFilter value={filterDining} onChange={setFilterDining} options={diningOptions} placeholder={t('all_dining')} />

                                {hasActiveFilters && (
                                    <button
                                        onClick={() => { setSearchQuery(''); setFilterCashier(''); setFilterPayment(''); setFilterDining(''); }}
                                        style={{
                                            padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.border}`,
                                            background: C.white, color: C.red, fontSize: 12, fontWeight: 600,
                                            cursor: 'pointer', fontFamily: FONT,
                                        }}
                                    >
                                        {t('clear_filters')}
                                    </button>
                                )}

                                {/* Count */}
                                <span style={{ fontSize: 12, color: C.mutedLight, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                                    {total} {total === 1 ? t('transaction') : t('transactions')}
                                </span>
                            </div>

                            {/* ── Table ── */}
                            {items.length === 0 ? (
                                <EmptyState
                                    icon={<IconNoData />}
                                    title={t('no_transactions_found')}
                                    subtitle={hasActiveFilters ? t('try_adjusting_filters') : t('transactions_will_appear_here')}
                                />
                            ) : isMobile ? (
                                <div style={{ padding: 12 }}>
                                    {items.map((row, i) => (
                                        <MobileCard
                                            key={row.transaction_id || i}
                                            avatar={{ initials: (row.staff_name || '?').slice(0, 2).toUpperCase() }}
                                            title={`#${row.transaction_id}`}
                                            amount={`${currency}${parseFloat(row.final_amount || 0).toFixed(2)}`}
                                            status={{ label: translateDynamic(row.payment_method, t), tone: row.payment_method === 'Cash' ? 'green' : 'gray' }}
                                            subtitle={[row.time, row.staff_name].filter(Boolean).join(' · ')}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                                    <span style={{ color: C.muted }}>{t('products')}</span>
                                                    <span style={{ fontWeight: 600, color: C.text, textAlign: 'right' }}>{getProductDisplay(row)}</span>
                                                </div>
                                                {row.dining_option && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                                        <span style={{ color: C.muted }}>{t('dining_option')}</span>
                                                        <span style={{ fontWeight: 600, color: C.text }}>{translateDynamic(row.dining_option, t)}</span>
                                                    </div>
                                                )}
                                                {row.session_id && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                                        <span style={{ color: C.muted }}>{t('session')}</span>
                                                        <span style={{ fontWeight: 600, color: C.text, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{row.session_id}</span>
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                                    <span style={{ color: C.muted }}>{t('total')}</span>
                                                    <span style={{ fontWeight: 800, color: C.text }}>{currency}{parseFloat(row.final_amount || 0).toFixed(2)}</span>
                                                </div>
                                                {isCurrentDay && (
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                                        <button onClick={(e) => { e.stopPropagation(); handleReprintReceipt(row); }}
                                                            style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.blue, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                                                            {t('reprint_receipt')}
                                                        </button>
                                                        <button onClick={(e) => {
                                                            e.stopPropagation();
                                                            deletionConfirm.confirmDeletion({
                                                                onDelete: async (reason) => { await handleDeleteTransaction(row.transaction_id, reason); },
                                                                title: t('delete_transaction'),
                                                                message: t('confirm_delete_transaction'),
                                                                itemName: `${t('transaction')} #${row.transaction_id} — ${currency}${parseFloat(row.final_amount).toFixed(2)}`,
                                                                requireReason: true,
                                                            });
                                                        }}
                                                            style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1px solid #fecaca`, background: C.redLight, color: C.red, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                                                            {t('void_transaction')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </MobileCard>
                                    ))}
                                </div>
                            ) : (
                                <div className="portal-table-wrap" style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                                        <thead>
                                            <tr style={{ background: C.bg }}>
                                                <th style={{ ...TH, cursor: 'pointer' }} onClick={() => toggleSort('time')}>
                                                    {t('time')} <SortArrow field="time" />
                                                </th>
                                                <th style={TH}>{t('session')}</th>
                                                <th style={TH}>{t('cashier')}</th>
                                                <th style={TH}>{t('order_number')}</th>
                                                <th style={TH}>{t('products')}</th>
                                                <th style={TH}>{t('dining_option')}</th>
                                                <th style={TH}>{t('payment')}</th>
                                                <th style={{ ...TH, cursor: 'pointer', textAlign: 'right' }} onClick={() => toggleSort('amount')}>
                                                    {t('total')} <SortArrow field="amount" />
                                                </th>
                                                {isCurrentDay && <th style={{ ...TH, textAlign: 'center' }}>{t('actions')}</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((row, i) => (
                                                <tr
                                                    key={row.transaction_id || i}
                                                    style={{ transition: 'background 0.1s' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                >
                                                    <td style={{ ...TD, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.time || '—'}</td>
                                                    <td style={{ ...TD, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: C.muted, letterSpacing: 0.3 }}>
                                                        {row.session_id || '—'}
                                                    </td>
                                                    <td style={TD}>{row.staff_name || '—'}</td>
                                                    <td style={{ ...TD, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: C.muted, letterSpacing: 0.3 }}>
                                                        #{row.transaction_id}
                                                    </td>
                                                    <td style={TD}>{getProductDisplay(row)}</td>
                                                    <td style={TD}>
                                                        {row.dining_option ? (
                                                            <span style={{
                                                                fontSize: 11, fontWeight: 600, letterSpacing: 0.3, borderRadius: 5, padding: '2px 8px',
                                                                background: C.blueLight, color: C.blue, fontFamily: FONT, whiteSpace: 'nowrap',
                                                            }}>
                                                                {translateDynamic(row.dining_option, t)}
                                                            </span>
                                                        ) : <span style={{ color: C.mutedLight }}>—</span>}
                                                    </td>
                                                    <td style={TD}>{paymentBadge(row.payment_method)}</td>
                                                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                                        {currency}{parseFloat(row.final_amount || 0).toFixed(2)}
                                                    </td>
                                                    {isCurrentDay && (
                                                    <td style={{ ...TD, textAlign: 'center' }}>
                                                        <div style={{ display: 'inline-flex', gap: 4 }}>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleReprintReceipt(row); }}
                                                                title={t('reprint_receipt')}
                                                                style={{
                                                                    width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
                                                                    background: C.white, color: C.blue, cursor: 'pointer',
                                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                    transition: 'all 0.15s',
                                                                }}
                                                                onMouseEnter={e => { e.currentTarget.style.background = C.blueLight; e.currentTarget.style.borderColor = C.blue; }}
                                                                onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.border; }}
                                                            >
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    deletionConfirm.confirmDeletion({
                                                                        onDelete: async (reason) => { await handleDeleteTransaction(row.transaction_id, reason); },
                                                                        title: t('delete_transaction'),
                                                                        message: t('confirm_delete_transaction'),
                                                                        itemName: `${t('transaction')} #${row.transaction_id} — ${currency}${parseFloat(row.final_amount).toFixed(2)}`,
                                                                        requireReason: true,
                                                                    });
                                                                }}
                                                                title={t('void_transaction')}
                                                                style={{
                                                                    width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
                                                                    background: C.white, color: C.red, cursor: 'pointer',
                                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                    transition: 'all 0.15s',
                                                                }}
                                                                onMouseEnter={e => { e.currentTarget.style.background = C.redLight; e.currentTarget.style.borderColor = C.red; }}
                                                                onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.border; }}
                                                            >
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* ── Pagination ── */}
                            {items.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: `1px solid ${C.border}` }}>
                                    <span style={{ fontSize: 12, color: C.muted }}>
                                        {t('showing')} {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} {t('of')} {total}
                                    </span>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                                            style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: 13, fontWeight: 600, cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? 0.5 : 1, fontFamily: FONT }}>
                                            {t('previous')}
                                        </button>
                                        <span style={{ fontSize: 13, color: C.muted, alignSelf: 'center' }}>{page} / {totalPages}</span>
                                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                                            style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: 13, fontWeight: 600, cursor: page >= totalPages ? 'default' : 'pointer', opacity: page >= totalPages ? 0.5 : 1, fontFamily: FONT }}>
                                            {t('next')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* ─── Reprint Modal ────────────────────────────────────────────────── */}
            {reprintModalOpen && (
                <div
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                    }}
                    onClick={() => setReprintModalOpen(false)}
                >
                    <div
                        className="portal-modal-card"
                        style={{
                            background: C.white, borderRadius: 16, padding: 28, width: 420,
                            maxHeight: '80vh', overflowY: 'auto', fontFamily: FONT,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: 0 }}>{t('reprint_receipt')}</h3>
                            <button onClick={() => setReprintModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}>&times;</button>
                        </div>

                        <p style={{ fontSize: 13, color: C.muted, margin: '0 0 16px' }}>
                            {t('select_receipts_to_reprint')} #{transactionToReprint?.transaction_id}
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                            {[
                                { key: 'customer', label: t('customer_receipt') },
                                { key: 'cashier', label: t('cashier_copy') },
                                ...(tenantConfig?.printable_categories || [])
                                    .filter(cat => transactionToReprint?.products?.some(p => p.category === cat))
                                    .map(cat => ({ key: cat.toLowerCase().replace(/\s+/g, '_'), label: `${translateDynamic(cat, t)} ${t('order')}` })),
                                ...(transactionToReprint?.products?.some(p => !(tenantConfig?.printable_categories || []).includes(p.category))
                                    ? [{ key: 'others', label: t('other_items') }] : []),
                            ].map(({ key, label }) => (
                                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: C.text, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={!!selectedReceiptTypes[key]}
                                        onChange={() => setSelectedReceiptTypes(prev => ({ ...prev, [key]: !prev[key] }))}
                                        style={{ width: 16, height: 16, accentColor: C.green }}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setReprintModalOpen(false)}
                                style={{
                                    padding: '8px 20px', borderRadius: 8, border: `1px solid ${C.border}`,
                                    background: C.white, color: C.text, fontSize: 13, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: FONT,
                                }}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={handlePrintSelected}
                                style={{
                                    padding: '8px 20px', borderRadius: 8, border: 'none',
                                    background: C.green, color: C.white, fontSize: 13, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: FONT,
                                }}
                            >
                                {t('print_selected')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Deletion confirm modal ──────────────────────────────────────── */}
            <DeletionConfirmModal
                isOpen={deletionConfirm.isModalOpen}
                onClose={deletionConfirm.handleCancel}
                onConfirm={deletionConfirm.handleConfirm}
                loading={deletionConfirm.loading}
                {...deletionConfirm.modalProps}
            />
        </PortalLayout>
    );
};

export default PortalRecentTransactionsPage;
